import _ from 'lodash';
import React from 'react';
import FontAwesome from 'react-fontawesome';
import Lightbox from 'react-image-lightbox';
import Router, { Link } from 'react-router-dom';

import history from 'providers/history';

import Log from 'providers/log';

import { InstrumentsApi, NodeApi } from 'providers/api';

import BreadcrumbTrail from 'components/global/application/BreadcrumbTrail';
import FailsafeImg from 'components/global/application/FailsafeImg';
import SlickCarousel from 'components/global/application/SlickCarousel';
import Button from 'components/global/form/Button';
import ItemLabel from 'components/pages/inventory/detail/ItemLabel';
import Disclaimer from 'components/pages/inventory/Disclaimer';
import NotFoundPage from 'components/pages/notFound/NotFoundPage';

import CartItem from 'types/shopify/CartItem';

import { CategoryMapping, DetailInstrument } from 'types/drupal/InstrumentNode';

import { InstrumentGroup } from 'types/drupal/InstrumentGroupNode';

import { categoryEncode, imageUrlAppendStyle } from 'contexts/link_util';

import { Helmet } from 'react-helmet';

import InstrumentGroupDetail from 'components/pages/inventory/detail/InstrumentGroupDetail';

import { instrumentImageAlt, instrumentImageUrl } from 'contexts/instrument';

import {
    getPrice,
    renderPrice
} from 'contexts/prices';

import 'react-image-lightbox/style.css';
import './InventoryDetail.sass';

const staticPathNodes: { label: string; route: string; }[] = [
    { label: 'Home', route: '/' },
    { label: 'Instruments & Amps', route: '/inventory' }
];

export interface OwnProps {
    match: Router.match<{ }>;
}

export interface StateProps {
    category_options: CategoryMapping[];
    cartItems: CartItem[];
    isMobile: boolean;
    product: any;
}

export interface DispatchProps {
    addToCart(item: CartItem): void;
    displayCartMessage(value: string): void;
    removeItem(item: CartItem): void;
}

interface State {
    isLighboxOpen: boolean;
    instrumentGroups: InstrumentGroup[];
    instrumentGroupsLoaded: boolean;
    item: DetailInstrument;
    itemLoaded: boolean;
    lightboxIndex: number;
    notFound: boolean;
    slideImageIndex: number;
}

type CombinedProps = DispatchProps & StateProps & OwnProps;

// TODO: Should pull this out into redux so we can simulate passing it in as a prop
// as we should if we had an actual API
// const item = guitarFactory.one();

class InventoryDetail extends React.Component<CombinedProps, State> {
    private messageTimeout: number;
    constructor(props: CombinedProps) {
        super(props);

        this.state = {
            instrumentGroups: null,
            instrumentGroupsLoaded: false,
            isLighboxOpen: false,
            item: null,
            itemLoaded: false,
            lightboxIndex: 0,
            notFound: false,
            slideImageIndex: 0
        };
    }
    // API call would never finish executing before the first render. Using didmount instead of willmount makes that understanding explicit
    public componentDidMount() {
        const targetPath = this.props.match.url.slice(1);   // slice off leading slash
        InstrumentsApi.getInstrument(targetPath).then((item: DetailInstrument) => {
            if (item.field_instr_do_not_list !== null && item.field_instr_do_not_list.value === '1') {
                history.push('/inventory');
                return;
            }
            this.setState({ item, itemLoaded: true });

            this.loadInstrumentGroups();

        }).catch((reason) => {
            if (reason.status === 404) this.setState({ notFound: true });

            Log.error(reason);
        });

    }

    public loadInstrumentGroups() {
        const referenceNid = this.state.item.nid;

        NodeApi.getInstrumentGroups(referenceNid).then((instrumentGroups: InstrumentGroup[]) => {
            this.setState({ instrumentGroups, instrumentGroupsLoaded: true });
        });

    }

    /**
     * Render lightbox for gallery
     */
    public renderLightbox = (images: any) => {
        if (!this.state.isLighboxOpen) {
            return;
        }
        const image = images[this.state.lightboxIndex];
        const nextIndex = (this.state.lightboxIndex + 1) % images.length;
        const nextImage = images[nextIndex];
        const prevIndex = (this.state.lightboxIndex + images.length - 1) % images.length;
        const prevImage = images[prevIndex];

        return (
            <Lightbox
                animationDisabled={true}
                imageTitle={image.title ? image.title : ''}
                mainSrc={image.file.url}
                nextSrc={nextImage.file.url}
                prevSrc={prevImage.file.url}
                onCloseRequest={() => this.setState({ isLighboxOpen: false })}
                onMovePrevRequest={() => this.setState({ lightboxIndex: prevIndex })}
                onMoveNextRequest={() => this.setState({ lightboxIndex: nextIndex })}
            />
        );
    }

    /**
     * check if user has the instrument in their cart
     */
    public getInstrumentFromCart = (productId: number, cartItems: CartItem[]) => {
        if (cartItems.length < 1) {
            return false;
        }
        if (cartItems.filter(item => item.productId === productId)) {
            return cartItems.filter(item => item.productId === productId)[0];
        }
        return false;
    }

    /**
     * render the instrument action
     * add to cart, remove from cart, contact us
     */
    public renderActions = (item: DetailInstrument) => {
        if (
            item.state === 'Available'
            && item.field_shopifyid
            && item.field_shopifyid.value
            && item.field_shopifyid_variant
            && item.field_shopifyid_variant.value
        ) {
            const cartItem = this.getInstrumentFromCart(parseInt(item.field_shopifyid.value, 0), this.props.cartItems);
            const button = cartItem
                ? <Button className="btn-contact" onClick={() => this.props.removeItem(cartItem)}>Remove from Cart</Button>
                : <Button className="btn-add-cart" onClick={this.handleAddButtonClicked}>Add To Cart</Button>;

            const affirmPrice = getPrice(item).toFixed(2).replace(/\./g, '');

            const affirmAttributes = {
                className: 'affirm-as-low-as',
                'data-amount': `${affirmPrice}`,
                'data-page-type': 'product'
            };

            return (
                <div>
                    <p {...affirmAttributes} />
                    {button}
                </div>
            );
        }
        return <Link to="/contactdirections"><Button className="btn-contact">Contact Us</Button></Link>;
    }

    public componentDidUpdate() {
        (window as any).affirm.ui.refresh();
    }

    public handleSlideClick = (imageIndex: number) => {
        this.setState({ slideImageIndex: imageIndex });
    }

    public clearCurrentMessage = (timeoutOnly?: boolean) => {
        if (this.messageTimeout > 0) window.clearTimeout(this.messageTimeout);
        if (!timeoutOnly) this.props.displayCartMessage('');
    }

    public handleAddButtonClicked = () => {
        const { item } = this.state;
        this.props.addToCart({
            addedTime: new Date(),
            field_gruhn_category_id: item.field_gruhn_category_id.value,
            imageSrc: item.field_instr_image[0].file.url ? item.field_instr_image[0].file.url : '',
            price: getPrice(item),
            productId: parseInt(item.field_shopifyid.value, 0),
            quantity: 1,
            title: item.title,
            type: 'Instrument',
            variantId: parseInt(item.field_shopifyid_variant.value, 0)
        });
        this.clearCurrentMessage(true);
        this.props.displayCartMessage(`${item.title} was`);
        this.messageTimeout = window.setTimeout(() => this.props.displayCartMessage(''), 15000, []);
    }

    public render() {
        const { item, instrumentGroups, itemLoaded, slideImageIndex, notFound } = this.state;

        if (notFound) return <NotFoundPage />;
        if (!itemLoaded) return (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />);

        const { category_options, cartItems, isMobile } = this.props;
        const currentCategory = category_options.find(v => v.tid === item.field_instr_category.tid);
        const expandedTitle = _.get(item, 'field_manufacturer.manufacturer.name') + ' ' + _.get(item, 'field_instr_model.value') + ' ' + _.get(item, 'field_instr_year.value');
        const colMediumLengths = this.colMediumLengths();

        return !itemLoaded // Negation so one line spinner can be first case

        ? (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />) // SPINNER
        : (
            <div className="row inventory-item">
                <Helmet>
                    <meta name="title" content={item.meta_title} />
                    <meta name="description" content={item.meta_description} />
                    <title>{item.meta_title}</title>
                </Helmet>
                <div className="col-10 offset-1">
                    <div className="row detail-breadcrumb">
                        <div className="col-12">
                            <BreadcrumbTrail
                                pathNodes={[
                                    ...staticPathNodes,
                                    { label: currentCategory.description, route: `/inventory/category/${categoryEncode(currentCategory.description)}` },
                                    { label: expandedTitle, route: `${this.props.match.url}` }
                                ]}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className={`${colMediumLengths[0]} col-sm-12`}>
                            {isMobile && <ItemLabel item={item} />}
                            <div className="image-wrapper">
                                <FailsafeImg
                                    className="main-image"
                                    src={instrumentImageUrl(item, 'home_featured_image', slideImageIndex)}
                                    alt={instrumentImageAlt(item, slideImageIndex)}
                                    onClick={(e) => {
                                        this.setState({
                                            isLighboxOpen: true,
                                            lightboxIndex: slideImageIndex
                                        });
                                    }}
                                />
                                <SlickCarousel
                                    imageSources={item.field_instr_image
                                        ? item.field_instr_image.map((k, i) =>  instrumentImageUrl(item, 'thumbnail', i))
                                        : ['']}
                                    onClick={this.handleSlideClick}
                                />
                                {this.renderLightbox(item.field_instr_image)}
                            </div>
                        </div>
                        <div className={`${colMediumLengths[1]} col-sm-12`}>
                            <ItemLabel item={item} />
                            <div className="description">{item.field_instr_product_description ? item.field_instr_product_description.value : ''}</div>
                            <div className="price">{renderPrice(item)}</div>
                            {this.renderActions(item)}
                            <Disclaimer />
                        </div>
                        <div className={`${colMediumLengths[2]} col-sm-12`}>
                            {this.instrumentGroups()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public instrumentGroups() {

        const { instrumentGroups, instrumentGroupsLoaded } = this.state;

        if (instrumentGroupsLoaded) {
            return (
            <div className="instrument-groups">
                {instrumentGroups.map(instrumentGroup =>
                    <InstrumentGroupDetail key={instrumentGroup.nid} instrument={this.state.item} instrumentGroup={instrumentGroup} />
                )}
            </div>
            );
        }
        return (<FontAwesome className="loading-spinner" name="spinner" size="3x" spin />);
    }

    public colMediumLengths() {

        const { instrumentGroups, instrumentGroupsLoaded } = this.state;

        if (!instrumentGroupsLoaded) {
            return ['col-md-3', 'col-md-8', 'col-md-1'];
        }

        if (instrumentGroupsLoaded && instrumentGroups.length > 0) {
            return ['col-md-3', 'col-md-6', 'col-md-3'];
        }

        return ['col-md-3', 'col-md-9', ''];
    }

}

export default InventoryDetail;



// WEBPACK FOOTER //
// ./src/components/pages/inventory/detail/InventoryDetail.tsx